import { useCallback, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useUIStore from '../stores/uiStore';
import useGameStore from '../stores/gameStore';

export const SCENE_BACK_NAVIGATION_MESSAGE =
  "You can't go back to a previous scene. Use the options on screen instead.";

const TOAST_DURATION_MS = 4000;
const TOAST_COOLDOWN_MS = 1500;
const BACK_GUARD_STATE_KEY = 'sceneBackGuard';

function buildScenePath(sessionId?: string, playerId?: string): string | null {
  if (!sessionId) {
    return null;
  }
  return playerId ? `/scene/${sessionId}/${playerId}` : `/scene/${sessionId}`;
}

export interface SceneBackNavigationGuardOptions {
  enabled?: boolean;
  onBackOptionSelect?: (optionId: string) => void;
}

/**
 * Intercepts browser / hardware back while on a scene page. Scene transitions do not
 * update the URL, so back would otherwise leave the game or return to Nexus.
 *
 * When the current scene has an available tagged back option, hardware back selects it.
 */
export function useSceneBackNavigationGuard(
  options: SceneBackNavigationGuardOptions | boolean = true
) {
  const resolvedOptions: SceneBackNavigationGuardOptions =
    typeof options === 'boolean' ? { enabled: options } : options;
  const { enabled = true, onBackOptionSelect } = resolvedOptions;

  const navigate = useNavigate();
  const { sessionId, playerId } = useParams();
  const showToast = useUIStore((state) => state.showToast);
  const lastToastAtRef = useRef(0);
  const onBackOptionSelectRef = useRef(onBackOptionSelect);
  onBackOptionSelectRef.current = onBackOptionSelect;

  const scenePath = buildScenePath(sessionId, playerId);

  const notifyBackBlocked = useCallback(() => {
    const now = Date.now();
    if (now - lastToastAtRef.current < TOAST_COOLDOWN_MS) {
      return;
    }
    lastToastAtRef.current = now;
    showToast(SCENE_BACK_NAVIGATION_MESSAGE, 'info', TOAST_DURATION_MS);
  }, [showToast]);

  useLayoutEffect(() => {
    if (!enabled || !scenePath) {
      return;
    }

    const pushGuardState = () => {
      window.history.pushState({ [BACK_GUARD_STATE_KEY]: true }, '', scenePath);
    };

    pushGuardState();

    const handlePopState = () => {
      const { actionPendingSince } = useUIStore.getState();
      if (actionPendingSince !== null) {
        pushGuardState();
        return;
      }

      const { backOptionId, availableOptionIds } = useGameStore.getState();
      if (backOptionId && availableOptionIds.includes(backOptionId)) {
        onBackOptionSelectRef.current?.(backOptionId);
        pushGuardState();
        return;
      }

      notifyBackBlocked();
      pushGuardState();
      navigate(scenePath, { replace: true });
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [enabled, scenePath, navigate, notifyBackBlocked]);
}
